// Save as new (spec 16 §3.12 item 3, §4 "Errors on save"). A name and a
// share box; the view stores the reader's current filters, order and columns,
// which the controller reads at the press, so the form holds no state of the
// list's own.
//
// Refusals land where §4 puts them. 409 `SAVED_VIEW_NAME_TAKEN` is a field
// error on the name. 409 `SAVED_VIEW_LIMIT_REACHED` is a form error that
// names the full cap from the problem's `cap` extension and points to Manage,
// with a control that opens it. D-305 ruling 31's 400 names the contact
// address a shared customer view may not keep. The client checks the name's
// length before any request, and the server checks it again.
//
// THE FORM KEEPS ITS KEY (V2, PR-E4 wave 6). The form holds one
// Idempotency-Key from its first send until the server answers. A send with no
// answer (offline, or an answer lost on the way back) keeps it and says the
// view may have been saved, and the retry sends the same key, so a first try
// that did land is replayed rather than saved twice (`../lib/write-key.ts`).

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { CheckboxField, Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { nameError, type ViewError } from "../lib/view-errors";
import { WriteKey } from "../lib/write-key";
import type { SavedView } from "../lib/view-state";
import type { SaveAsNewInput } from "../hooks/use-saved-views";
import { ViewErrorLine } from "./view-error-line";

export interface SaveViewModalProps {
  readonly onSubmit: (input: SaveAsNewInput) => Promise<SavedView>;
  readonly onClose: () => void;
  /** Opens Manage, offered when a cap is full. */
  readonly onOpenManage: () => void;
  /** The screen's own name for a filter a refusal names (W8). */
  readonly filterLabel?: (key: string) => string | undefined;
}

export function SaveViewModal({
  onSubmit,
  onClose,
  onOpenManage,
  filterLabel,
}: SaveViewModalProps) {
  const { t } = useTranslation();
  const nameId = useId();
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ViewError | undefined>(undefined);
  const [writeKey] = useState(() => new WriteKey());

  const submit = async () => {
    const local = nameError(name);
    if (local !== undefined) {
      setError(local);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit({ name, shared, idempotencyKey: writeKey.take() });
      writeKey.settle();
      onClose();
    } catch (caught) {
      setError(writeKey.settle(caught));
      setBusy(false);
    }
  };

  const nameFieldError = error?.target === "name" ? error : undefined;
  const formError = error?.target === "form" ? error : undefined;

  return (
    <Modal
      label={t("views.saveNew.title")}
      heading={t("views.saveNew.title")}
      closeLabel={t("views.saveNew.cancel")}
      onClose={onClose}
      pending={busy}
      className="views-modal"
      testId="views-save-modal"
    >
      <form
        className="views-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field label={t("views.saveNew.name")}>
          <input
            id={nameId}
            className="ds-input"
            value={name}
            autoComplete="off"
            aria-invalid={nameFieldError !== undefined}
            {...(nameFieldError !== undefined ? { "aria-describedby": `${nameId}-error` } : {})}
            data-testid="views-save-name"
            onChange={(event) => {
              setName(event.target.value);
              if (error?.target === "name") setError(undefined);
            }}
          />
        </Field>
        {nameFieldError !== undefined ? (
          <p
            id={`${nameId}-error`}
            className="field-error"
            role="alert"
            data-testid="views-save-name-error"
          >
            {t(nameFieldError.key, { ...nameFieldError.params })}
          </p>
        ) : null}
        <CheckboxField
          label={t("views.saveNew.share")}
          checked={shared}
          onChange={setShared}
          data-testid="views-save-shared"
        />
        {formError !== undefined ? (
          <ViewErrorLine error={formError} testId="views-save-error" filterLabel={filterLabel}>
            {formError.pointsToManage === true ? (
              <Button
                size="sm"
                onClick={() => {
                  onClose();
                  onOpenManage();
                }}
                data-testid="views-save-open-manage"
              >
                {t("views.saveNew.openManage")}
              </Button>
            ) : null}
          </ViewErrorLine>
        ) : null}
        <div className="form-actions">
          <Button size="sm" disabled={busy} onClick={onClose}>
            {t("views.saveNew.cancel")}
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            busy={busy}
            busyLabel={t("views.saveNew.submitting")}
            data-testid="views-save-submit"
          >
            {t("views.saveNew.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
